import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Download, FileText, ExternalLink } from 'lucide-react';
import { API_URL } from '../lib/api';

// Generate a deterministic color from a user/commercial id
const COLOR_PALETTE = [
    '#E85A24', // brand orange
    '#16A34A', // green
    '#0284C7', // blue
    '#7C3AED', // violet
    '#D97706', // amber
    '#DC2626', // red
    '#0891B2', // cyan
    '#DB2777', // pink
    '#65A30D', // lime
    '#9333EA', // purple
    '#EA580C', // deep orange
    '#059669', // emerald
];

export const colorFromId = (id) => {
    if (!id) return '#52525B';
    let hash = 0;
    for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
    return COLOR_PALETTE[hash % COLOR_PALETTE.length];
};

export const UserBadge = ({ userId, name, size = 'md', withName = true }) => {
    const color = colorFromId(userId);
    const initials = (name || '?').split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();
    const avatarSize = size === 'sm' ? 'w-6 h-6 text-[11px]' : 'w-8 h-8 text-xs';
    return (
        <span className="inline-flex items-center gap-2">
            <span
                className={`${avatarSize} rounded-full flex items-center justify-center text-white font-semibold shrink-0`}
                style={{ backgroundColor: color }}
            >
                {initials}
            </span>
            {withName && <span className="text-sm font-medium" style={{ color }}>{name || '—'}</span>}
        </span>
    );
};

export const DocumentPreview = ({ document, open, onOpenChange }) => {
    const [objectUrl, setObjectUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open || !document) return;
        let cancelled = false;
        let urlCreated = null;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await fetch(`${API_URL}/api/documents/${document.id}/download`, { credentials: 'include' });
                if (!res.ok) throw new Error('Chargement impossible');
                const blob = await res.blob();
                urlCreated = URL.createObjectURL(blob);
                if (!cancelled) setObjectUrl(urlCreated);
            } catch (e) {
                if (!cancelled) setError(e.message || 'Erreur');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
            if (urlCreated) URL.revokeObjectURL(urlCreated);
            setObjectUrl(null);
        };
    }, [open, document]);

    if (!document) return null;
    const type = document.content_type || '';
    const isImage = type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(document.original_filename || '');
    const isPdf = type === 'application/pdf' || /\.pdf$/i.test(document.original_filename || '');

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto" data-testid="document-preview-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center justify-between">
                        <span className="truncate pr-4">{document.original_filename}</span>
                        {objectUrl && (
                            <a
                                href={objectUrl}
                                download={document.original_filename}
                                className="text-sm text-[#E85A24] hover:underline flex items-center gap-1 shrink-0"
                            >
                                <Download className="w-4 h-4" /> Télécharger
                            </a>
                        )}
                    </DialogTitle>
                </DialogHeader>
                <div className="min-h-[300px] flex items-center justify-center">
                    {loading && (
                        <div className="w-8 h-8 border-4 border-[#E85A24] border-t-transparent rounded-full animate-spin" />
                    )}
                    {error && (
                        <div className="text-center text-[#DC2626]">
                            <p className="font-semibold mb-2">Impossible d'afficher le document</p>
                            <p className="text-sm">{error}</p>
                        </div>
                    )}
                    {!loading && !error && objectUrl && isImage && (
                        <img src={objectUrl} alt={document.original_filename} className="max-w-full max-h-[75vh] object-contain" />
                    )}
                    {!loading && !error && objectUrl && isPdf && (
                        <iframe src={objectUrl} className="w-full h-[75vh] border-0" title={document.original_filename} />
                    )}
                    {!loading && !error && objectUrl && !isImage && !isPdf && (
                        <div className="text-center">
                            <FileText className="w-12 h-12 mx-auto text-[#52525B] mb-3" />
                            <p className="font-medium mb-3">Aperçu non disponible pour ce format</p>
                            <Button asChild className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white">
                                <a href={objectUrl} target="_blank" rel="noreferrer" download={document.original_filename}>
                                    <ExternalLink className="w-4 h-4 mr-2" /> Ouvrir / Télécharger
                                </a>
                            </Button>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

// Fuzzy match helper: case-insensitive, all query tokens must appear in order (not contiguous)
export const fuzzyMatch = (haystack, needle) => {
    if (!needle) return true;
    if (!haystack) return false;
    const h = haystack.toLowerCase();
    const n = needle.toLowerCase().trim();
    if (h.includes(n)) return true;
    // tokens must each appear somewhere (AND)
    const tokens = n.split(/\s+/).filter(Boolean);
    if (tokens.every(t => h.includes(t))) return true;
    // character-sequence subset
    let i = 0;
    for (const c of h) {
        if (c === n[i]) i++;
        if (i === n.length) return true;
    }
    return false;
};

// Cover photo thumbnail (small, for list/kanban)
export const CoverThumb = ({ dossierId, hasPhoto, size = 40 }) => {
    if (!hasPhoto) return null;
    return (
        <img
            src={`${API_URL}/api/dossiers/${dossierId}/cover-photo`}
            alt="cover"
            className="rounded-sm object-cover border border-[#E4E4E7] bg-[#F4F4F5] shrink-0"
            style={{ width: size, height: size }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
    );
};
